import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ShieldAlert } from "lucide-react";

interface SafetyTermsDialogProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAccept: () => void;
  onCancel: () => void;
}

export function SafetyTermsDialog({ 
    trigger, 
    open, 
    onOpenChange,
    onAccept,
    onCancel
}: SafetyTermsDialogProps) {
  const [agreedTrafficking, setAgreedTrafficking] = useState(false);
  const [agreedChildSafety, setAgreedChildSafety] = useState(false);
  const [agreedReporting, setAgreedReporting] = useState(false);
  const acceptTerms = useMutation(api.auth.acceptSafetyTerms);

  const canProceed = agreedTrafficking && agreedChildSafety && agreedReporting;

  const handleAccept = async () => {
    if (!canProceed) return;
    try {
        await acceptTerms();
        onAccept();
        if (onOpenChange) onOpenChange(false);
    } catch (e) {
        console.error("Failed to accept terms", e);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-destructive mb-2">
            <ShieldAlert className="w-6 h-6" />
            <AlertDialogTitle className="text-xl">Safety & Community Pledge</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base text-foreground">
            To access Personal Connections, you must explicitly agree to our zero-tolerance safety policies. 
            We cooperate fully with law enforcement to prevent exploitation.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-start space-x-3 p-3 border rounded-md bg-muted/30">
            <Checkbox 
                id="term-trafficking" 
                checked={agreedTrafficking} 
                onCheckedChange={(c) => setAgreedTrafficking(c === true)} 
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="term-trafficking"
                className="text-sm font-semibold leading-snug cursor-pointer"
              >
                I confirm I will NOT use this platform for human trafficking, prostitution, or any form of commercial sexual exploitation.
              </Label>
              <p className="text-xs text-muted-foreground">Violations result in immediate bans and reporting to authorities (e.g., NCMEC, DHS).</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 border rounded-md bg-muted/30">
            <Checkbox 
                id="term-child" 
                checked={agreedChildSafety} 
                onCheckedChange={(c) => setAgreedChildSafety(c === true)} 
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="term-child"
                className="text-sm font-semibold leading-snug cursor-pointer"
              >
                I confirm I will NOT post content involving, depicting, or soliciting minors.
              </Label>
              <p className="text-xs text-muted-foreground">Zero tolerance. We use AI scanning to detect and report child exploitation attempts.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 border rounded-md bg-muted/30">
            <Checkbox 
                id="term-report" 
                checked={agreedReporting} 
                onCheckedChange={(c) => setAgreedReporting(c === true)} 
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="term-report"
                className="text-sm font-semibold leading-snug cursor-pointer"
              >
                I agree that my account activity can be audited for safety compliance and shared with law enforcement if illegal activity is detected.
              </Label>
            </div>
          </div>
        </div>

        <AlertDialogFooter className="sm:justify-between">
            <div className="flex-1"></div>
            <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
                onClick={(e) => {
                    e.preventDefault(); 
                    handleAccept();
                }}
                disabled={!canProceed}
                className={!canProceed ? "opacity-50 cursor-not-allowed" : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}
            >
            Accept & Enter
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
